import React, { useEffect, useState } from 'react';
import Navigation from '../components/Navigation';
import { CheckCircle, XCircle, Users, Package, FileText, Zap, Star, Crown, TrendingUp, AlertTriangle } from '../components/Icons';
import { toast } from 'react-hot-toast';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

const Subscription = () => {
  const [plans, setPlans] = useState({});
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      const [plansRes, currentRes, usageRes] = await Promise.all([
        apiGet('/api/subscription/plans'),
        apiGet('/api/subscription/current'),
        apiGet('/api/subscription/usage')
      ]);

      if (plansRes.ok) {
        const plansData = plansRes.data.plans;
        setPlans(plansData);
      }

      if (currentRes.ok) {
        const currentData = currentRes.data.subscription;
        setCurrentSubscription(currentData);
      }

      if (usageRes.ok) {
        const usageData = usageRes.data;
        setUsage(usageData);
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      toast.error('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const subscribeToPlan = async (planName) => {
    try {
      const planInfo = plans[planName];
      const res = await apiPost('/api/subscription/subscribe', {
        plan_name: planName,
        amount: planInfo.price,
        currency: 'PKR'
      });

      if (res.ok) {
        const data = res.data;
        toast.success(data.message);
        fetchSubscriptionData(); // Refresh data
      } else {
        const error = res.data;
        toast.error(`Subscription failed: ${error.detail}`);
      }
    } catch (error) {
      toast.error('Error subscribing to plan');
    }
  };

  const upgradeToPlan = async () => {
    if (!selectedPlan) return;

    try {
      const res = await apiPost('/api/subscription/upgrade', { new_plan: selectedPlan });

      if (res.ok) {
        const data = res.data;
        toast.success(data.message);
        setShowUpgradeModal(false);
        setSelectedPlan(null);
        fetchSubscriptionData(); // Refresh data
      } else {
        const error = res.data;
        toast.error(`Upgrade failed: ${error.detail}`);
      }
    } catch (error) {
      toast.error('Error upgrading plan');
    }
  };

  const cancelSubscription = async () => {
    if (!currentSubscription?.subscription) return;

    if (!window.confirm('Are you sure you want to cancel your subscription? This action cannot be undone.')) {
      return;
    }

    try {
      const res = await apiDelete(`/api/subscription/${currentSubscription.subscription.id}`);

      if (res.ok) {
        toast.success('Subscription cancelled successfully');
        fetchSubscriptionData(); // Refresh data
      } else {
        const error = res.data;
        toast.error(`Cancellation failed: ${error.detail}`);
      }
    } catch (error) {
      toast.error('Error cancelling subscription');
    }
  };

  const getPlanIcon = (planName) => {
    switch (planName) {
      case 'basic': return <Package className="h-8 w-8" />;
      case 'pro': return <Zap className="h-8 w-8" />;
      case 'enterprise': return <Crown className="h-8 w-8" />;
      default: return <Package className="h-8 w-8" />;
    }
  };

  const getPlanColor = (planName) => {
    switch (planName) {
      case 'basic': return 'border-gray-300 bg-white';
      case 'pro': return 'border-blue-500 bg-blue-50';
      case 'enterprise': return 'border-purple-600 bg-purple-50';
      default: return 'border-gray-300 bg-white';
    }
  };

  const getUsagePercentage = (current, limit) => {
    if (limit === -1) return 0; // Unlimited
    if (limit === 0) return 100;
    return Math.min((current / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <Navigation>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading subscription information...</p>
          </div>
        </div>
      </Navigation>
    );
  }

  return (
    <Navigation>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
                <p className="text-gray-600">Manage your subscription plan and usage</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Current Subscription Status */}
          {currentSubscription && (
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Subscription</h2>
                {currentSubscription.status === 'active' ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                      <div>
                        <h3 className="text-lg font-medium text-green-800">
                          {currentSubscription.plan_info.name} Plan
                        </h3>
                        <p className="text-green-700">
                          Active until {new Date(currentSubscription.subscription.current_period_end).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 flex space-x-3">
                      <button
                        onClick={() => {
                          setSelectedPlan('pro');
                          setShowUpgradeModal(true);
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                      >
                        Upgrade Plan
                      </button>
                      <button
                        onClick={cancelSubscription}
                        className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                      >
                        Cancel Subscription
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <XCircle className="h-6 w-6 text-gray-600 mr-3" />
                      <div>
                        <h3 className="text-lg font-medium text-gray-800">No Active Subscription</h3>
                        <p className="text-gray-700">Subscribe to a plan to unlock all features</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Usage Statistics */}
          {usage && (
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Usage Statistics</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Users className="h-6 w-6 text-blue-600 mr-2" />
                      <span className="text-2xl font-bold text-gray-900">{usage.usage?.users || 0}</span>
                    </div>
                    <p className="text-sm text-gray-600">Active Users</p>
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${getUsageColor(getUsagePercentage(usage.usage?.users || 0, usage.limits.max_users))}`}
                          style={{ width: `${getUsagePercentage(usage.usage?.users || 0, usage.limits.max_users)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {usage.limits.max_users === -1 ? 'Unlimited' : `${usage.usage?.users || 0} / ${usage.limits.max_users}`}
                      </p>
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Package className="h-6 w-6 text-green-600 mr-2" />
                      <span className="text-2xl font-bold text-gray-900">{usage.usage?.products || 0}</span>
                    </div>
                    <p className="text-sm text-gray-600">Products</p>
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${getUsageColor(getUsagePercentage(usage.usage?.products || 0, usage.limits.max_products))}`}
                          style={{ width: `${getUsagePercentage(usage.usage?.products || 0, usage.limits.max_products)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {usage.limits.max_products === -1 ? 'Unlimited' : `${usage.usage?.products || 0} / ${usage.limits.max_products}`}
                      </p>
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <FileText className="h-6 w-6 text-purple-600 mr-2" />
                      <span className="text-2xl font-bold text-gray-900">{usage.usage?.invoices_this_month || 0}</span>
                    </div>
                    <p className="text-sm text-gray-600">Invoices This Month</p>
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${getUsageColor(getUsagePercentage(usage.usage?.invoices_this_month || 0, usage.limits.max_invoices))}`}
                          style={{ width: `${getUsagePercentage(usage.usage?.invoices_this_month || 0, usage.limits.max_invoices)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {usage.limits.max_invoices === -1 ? 'Unlimited' : `${usage.usage?.invoices_this_month || 0} / ${usage.limits.max_invoices}`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Available Plans */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Available Plans</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {Object.entries(plans).map(([planKey, plan]) => (
                  <div
                    key={planKey}
                    className={`border-2 rounded-lg p-6 ${getPlanColor(planKey)} ${
                      currentSubscription?.subscription?.plan_name === planKey ? 'ring-2 ring-blue-500' : ''
                    }`}
                  >
                    <div className="text-center mb-6">
                      <div className="flex justify-center mb-4">
                        {getPlanIcon(planKey)}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                      <div className="text-3xl font-bold text-gray-900 mb-1">
                        Rs. {plan.price.toLocaleString()}
                      </div>
                      <p className="text-gray-600">per month</p>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="text-center">
                      {currentSubscription?.subscription?.plan_name === planKey ? (
                        <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-md font-medium">
                          Current Plan
                        </div>
                      ) : currentSubscription?.status === 'active' ? (
                        <button
                          onClick={() => {
                            setSelectedPlan(planKey);
                            setShowUpgradeModal(true);
                          }}
                          className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 font-medium"
                        >
                          Upgrade to {plan.name}
                        </button>
                      ) : (
                        <button
                          onClick={() => subscribeToPlan(planKey)}
                          className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 font-medium"
                        >
                          Subscribe to {plan.name}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Upgrade Modal */}
        {showUpgradeModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Upgrade Subscription</h3>
                <p className="text-sm text-gray-500 mb-6">
                  Are you sure you want to upgrade to the {plans[selectedPlan]?.name} plan?
                  This will cancel your current subscription and start a new billing cycle.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowUpgradeModal(false);
                      setSelectedPlan(null);
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={upgradeToPlan}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    Confirm Upgrade
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Navigation>
  );
};

export default Subscription;
